import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { 
  Mic, Bot, BookOpen, Wrench, FlaskConical, Plus, Edit, Trash2, 
  RefreshCw, Search, CheckCircle, XCircle, Play, MessageSquare,
  Volume2, Settings, AlertCircle, Send, Copy, ChevronRight,
  Users, FileText, Zap, Shield, Square, Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Voice options for TTS
const VOICE_OPTIONS = [
  { id: 'nova', label: 'Nova (Clear, Energetic)' },
  { id: 'alloy', label: 'Alloy (Neutral)' },
  { id: 'echo', label: 'Echo (Warm)' },
  { id: 'fable', label: 'Fable (Expressive)' },
  { id: 'onyx', label: 'Onyx (Deep)' },
  { id: 'shimmer', label: 'Shimmer (Soft)' }
];

// Agent role presets
const ROLE_PRESETS = [
  { role: 'sales', label: 'Sales', color: 'bg-green-500' },
  { role: 'receptionist', label: 'Receptionist', color: 'bg-blue-500' },
  { role: 'support', label: 'Support', color: 'bg-purple-500' },
  { role: 'billing', label: 'Billing', color: 'bg-orange-500' },
  { role: 'custom', label: 'Custom', color: 'bg-gray-500' }
];

// KB Categories
const KB_CATEGORIES = [
  'FAQ',
  'Policy',
  'Product',
  'Service',
  'Pricing',
  'Hours',
  'Location',
  'Contact',
  'Other'
];

// ==========================================
// Stats Overview Component
// ==========================================
const StatsOverview = ({ stats, config }) => {
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Agent Profiles</p>
              <p className="text-2xl font-bold text-blue-500">{stats?.agent_profiles?.active || 0}</p>
              <p className="text-xs text-muted-foreground">{stats?.agent_profiles?.total || 0} total</p>
            </div>
            <Bot className="h-8 w-8 text-blue-500/30" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Knowledge Items</p>
              <p className="text-2xl font-bold text-green-500">{stats?.knowledge_base?.active || 0}</p>
              <p className="text-xs text-muted-foreground">{stats?.knowledge_base?.total || 0} total</p>
            </div>
            <BookOpen className="h-8 w-8 text-green-500/30" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Tools Enabled</p>
              <p className="text-2xl font-bold text-purple-500">{stats?.tools?.enabled || 0}</p>
              <p className="text-xs text-muted-foreground">{stats?.tools?.total || 0} registered</p>
            </div>
            <Wrench className="h-8 w-8 text-purple-500/30" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Tests (24h)</p>
              <p className="text-2xl font-bold text-orange-500">{stats?.test_runs_24h || 0}</p>
              <p className="text-xs text-muted-foreground">
                KB: {config?.voice_kb_enabled ? 
                  <span className="text-green-500">ON</span> : 
                  <span className="text-red-500">OFF</span>}
              </p>
            </div>
            <FlaskConical className="h-8 w-8 text-orange-500/30" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==========================================
// Agent Profiles Tab
// ==========================================
const AgentProfilesTab = ({ onRefresh }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: 'receptionist',
    description: '',
    voice_id: 'nova',
    voice_speed: 1.0,
    system_prompt: '',
    goals: [],
    greeting_message: '',
    fallback_message: 'Let me connect you with someone who can help.',
    greeting_delay_ms: 500,
    response_delay_ms: 300,
    end_of_speech_wait_ms: 800,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [goalsInput, setGoalsInput] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const audioRef = useRef(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/voice-admin/agents`);
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('Failed to load agent profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Voice preview function
  const handlePreviewVoice = async () => {
    const previewText = formData.greeting_message || 'Hello! Thank you for calling. How can I help you today?';
    
    setPreviewLoading(true);
    try {
      const response = await axios.post(`${API}/voice-admin/preview-voice`, {
        text: previewText,
        voice_id: formData.voice_id,
        speed: formData.voice_speed
      });
      
      if (response.data.success && response.data.audio_base64) {
        // Stop any existing playback
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        
        // Create audio from base64
        const audioBlob = new Blob(
          [Uint8Array.from(atob(response.data.audio_base64), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onplay = () => setPreviewPlaying(true);
        audio.onended = () => {
          setPreviewPlaying(false);
          URL.revokeObjectURL(audioUrl);
        };
        audio.onerror = () => {
          setPreviewPlaying(false);
          toast.error('Failed to play audio');
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate voice preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPreviewPlaying(false);
    }
  };

  const handleNewProfile = () => {
    setSelectedProfile(null);
    setFormData({
      name: '',
      role: 'receptionist',
      description: '',
      voice_id: 'nova',
      voice_speed: 1.0,
      system_prompt: '',
      goals: [],
      greeting_message: 'Hello! Thank you for calling. How can I help you today?',
      fallback_message: 'Let me connect you with someone who can help.',
      greeting_delay_ms: 500,
      response_delay_ms: 300,
      end_of_speech_wait_ms: 800,
      is_active: true
    });
    setGoalsInput('');
    setEditDialogOpen(true);
  };

  const handleEditProfile = (profile) => {
    setSelectedProfile(profile);
    setFormData({
      name: profile.name || '',
      role: profile.role || 'receptionist',
      description: profile.description || '',
      voice_id: profile.voice_id || 'nova',
      voice_speed: profile.voice_speed || 1.0,
      system_prompt: profile.system_prompt || '',
      goals: profile.goals || [],
      greeting_message: profile.greeting_message || '',
      fallback_message: profile.fallback_message || 'Let me connect you with someone who can help.',
      greeting_delay_ms: profile.greeting_delay_ms ?? 500,
      response_delay_ms: profile.response_delay_ms ?? 300,
      end_of_speech_wait_ms: profile.end_of_speech_wait_ms ?? 800,
      is_active: profile.is_active !== false
    });
    setGoalsInput((profile.goals || []).join('\n'));
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!formData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        goals: goalsInput.split('\n').filter(g => g.trim())
      };

      if (selectedProfile?.id) {
        await axios.put(`${API}/voice-admin/agents/${selectedProfile.id}`, payload);
        toast.success('Agent profile updated');
      } else {
        await axios.post(`${API}/voice-admin/agents`, payload);
        toast.success('Agent profile created');
      }
      
      setEditDialogOpen(false);
      fetchProfiles();
      onRefresh?.();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to deactivate this profile?')) return;
    
    try {
      await axios.delete(`${API}/voice-admin/agents/${profileId}`);
      toast.success('Profile deactivated');
      fetchProfiles();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const getRoleColor = (role) => {
    const preset = ROLE_PRESETS.find(r => r.role === role);
    return preset?.color || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Configure AI agent personas for different call scenarios
        </p>
        <Button onClick={handleNewProfile}>
          <Plus className="h-4 w-4 mr-2" />
          New Profile
        </Button>
      </div>

      {profiles.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {profiles.map((profile) => (
            <Card key={profile.id} className={!profile.is_active ? 'opacity-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${getRoleColor(profile.role)} flex items-center justify-center`}>
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium">{profile.name}</h3>
                      <Badge variant="outline" className="text-xs capitalize">
                        {profile.role}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEditProfile(profile)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteProfile(profile.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {profile.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {profile.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Volume2 className="h-3 w-3" />
                    {VOICE_OPTIONS.find(v => v.id === profile.voice_id)?.label?.split(' ')[0] || profile.voice_id}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    {profile.goals?.length || 0} goals
                  </span>
                  {profile.is_active ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                      Inactive
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Bot className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No agent profiles yet</p>
          <Button variant="link" onClick={handleNewProfile}>
            Create your first profile
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProfile ? 'Edit Agent Profile' : 'New Agent Profile'}
            </DialogTitle>
            <DialogDescription>
              Configure the AI agent&apos;s personality, voice, and behavior
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Profile Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sales Agent, Front Desk"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_PRESETS.map((preset) => (
                      <SelectItem key={preset.role} value={preset.role}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this agent's purpose"
              />
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Voice Settings</Label>
                <Button
                  type="button"
                  variant={previewPlaying ? "destructive" : "outline"}
                  size="sm"
                  onClick={previewPlaying ? handleStopPreview : handlePreviewVoice}
                  disabled={previewLoading}
                >
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : previewPlaying ? (
                    <Square className="h-4 w-4 mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {previewPlaying ? 'Stop' : 'Preview Voice'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Voice</Label>
                  <Select 
                    value={formData.voice_id} 
                    onValueChange={(v) => setFormData({ ...formData, voice_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOICE_OPTIONS.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          {voice.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Voice Speed ({formData.voice_speed}x)</Label>
                  <Input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={formData.voice_speed}
                    onChange={(e) => setFormData({ ...formData, voice_speed: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <Label className="text-base font-medium">Timing Settings</Label>
              <p className="text-xs text-muted-foreground -mt-2">
                Adjust delays to make conversations feel more natural
              </p>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Greeting Delay</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="range"
                      min="0"
                      max="2000"
                      step="100"
                      value={formData.greeting_delay_ms}
                      onChange={(e) => setFormData({ ...formData, greeting_delay_ms: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground w-12">{formData.greeting_delay_ms}ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Wait before greeting</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Response Delay</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="range"
                      min="0"
                      max="1500"
                      step="100"
                      value={formData.response_delay_ms}
                      onChange={(e) => setFormData({ ...formData, response_delay_ms: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground w-12">{formData.response_delay_ms}ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Pause before responding</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Speech Wait</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="range"
                      min="300"
                      max="2000"
                      step="100"
                      value={formData.end_of_speech_wait_ms}
                      onChange={(e) => setFormData({ ...formData, end_of_speech_wait_ms: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground w-12">{formData.end_of_speech_wait_ms}ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Wait after user stops</p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Greeting Message</Label>
              <Textarea
                value={formData.greeting_message}
                onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                placeholder="Hello! Thank you for calling..."
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                This message will be spoken when the call connects. Use the Preview button above to hear it.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>System Prompt (AI Instructions)</Label>
              <Textarea
                value={formData.system_prompt}
                onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                placeholder="You are a helpful assistant for..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Goals (one per line)</Label>
              <Textarea
                value={goalsInput}
                onChange={(e) => setGoalsInput(e.target.value)}
                placeholder="Schedule appointments&#10;Answer basic questions&#10;Collect contact information"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fallback Message (when uncertain)</Label>
              <Input
                value={formData.fallback_message}
                onChange={(e) => setFormData({ ...formData, fallback_message: e.target.value })}
                placeholder="Let me connect you with someone who can help."
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {selectedProfile ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// Knowledge Base Tab
// ==========================================
const KnowledgeBaseTab = ({ onRefresh }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'FAQ',
    content: '',
    tags: '',
    keywords: '',
    priority: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/voice-admin/knowledge?${params}`);
      setItems(response.data.items || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching KB items:', error);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleNewItem = () => {
    setSelectedItem(null);
    setFormData({
      title: '',
      category: 'FAQ',
      content: '',
      tags: '',
      keywords: '',
      priority: 0,
      is_active: true
    });
    setEditDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || '',
      category: item.category || 'FAQ',
      content: item.content || '',
      tags: (item.tags || []).join(', '),
      keywords: (item.keywords || []).join(', '),
      priority: item.priority || 0,
      is_active: item.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
      };

      if (selectedItem?.id) {
        await axios.put(`${API}/voice-admin/knowledge/${selectedItem.id}`, payload);
        toast.success('Knowledge item updated');
      } else {
        await axios.post(`${API}/voice-admin/knowledge`, payload);
        toast.success('Knowledge item created');
      }
      
      setEditDialogOpen(false);
      fetchItems();
      onRefresh?.();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to deactivate this item?')) return;
    
    try {
      await axios.delete(`${API}/voice-admin/knowledge/${itemId}`);
      toast.success('Item deactivated');
      fetchItems();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search knowledge base..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {(categories.length > 0 ? categories : KB_CATEGORIES).map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleNewItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length > 0 ? (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.category}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <div className="flex flex-wrap gap-1">
                      {(item.tags || []).slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {(item.tags || []).length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{item.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{item.priority}</TableCell>
                  <TableCell>
                    {item.is_active ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => handleEditItem(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No knowledge items found</p>
          <Button variant="link" onClick={handleNewItem}>
            Add your first item
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedItem ? 'Edit Knowledge Item' : 'New Knowledge Item'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Business Hours"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KB_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="The full answer or information..."
                rows={6}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="hours, schedule, open"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Keywords (comma-separated)</Label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="when, open, close, hours"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="space-y-2">
                <Label>Priority (higher = more important)</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  className="w-24"
                />
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveItem} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {selectedItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// Tools Tab
// ==========================================
const ToolsTab = ({ onRefresh }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/voice-admin/tools`);
      setTools(response.data.tools || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const toggleTool = async (toolId, enabled) => {
    try {
      await axios.put(`${API}/voice-admin/tools/${toolId}`, { is_enabled: enabled });
      toast.success(`Tool ${enabled ? 'enabled' : 'disabled'}`);
      fetchTools();
    } catch (error) {
      toast.error('Failed to update tool');
    }
  };

  // Default tools to show if none registered
  const defaultTools = [
    { tool_id: 'transfer_call', name: 'Transfer Call', description: 'Transfer the caller to a human agent', category: 'routing', is_enabled: true },
    { tool_id: 'schedule_callback', name: 'Schedule Callback', description: 'Schedule a callback for the caller', category: 'scheduling', is_enabled: true },
    { tool_id: 'send_sms', name: 'Send SMS', description: 'Send an SMS message to the caller', category: 'communication', is_enabled: false },
    { tool_id: 'lookup_appointment', name: 'Lookup Appointment', description: 'Look up existing appointments for a customer', category: 'data', is_enabled: true },
  ];

  const displayTools = tools.length > 0 ? tools : defaultTools;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Tools that voice agents can use during calls (Phase 1: dry-run only)
          </p>
        </div>
        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <AlertCircle className="h-3 w-3 mr-1" />
          Dry-Run Mode
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {displayTools.map((tool) => (
          <Card key={tool.tool_id || tool.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${tool.is_enabled ? 'bg-green-500/10' : 'bg-gray-500/10'} flex items-center justify-center`}>
                    <Wrench className={`h-5 w-5 ${tool.is_enabled ? 'text-green-500' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-medium">{tool.name}</h3>
                    <Badge variant="outline" className="text-xs">
                      {tool.category}
                    </Badge>
                  </div>
                </div>
                <Switch
                  checked={tool.is_enabled}
                  onCheckedChange={(v) => toggleTool(tool.tool_id, v)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {tool.description}
              </p>
              {tool.requires_confirmation !== false && (
                <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  Requires confirmation
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// Test Harness Tab
// ==========================================
const TestHarnessTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await axios.get(`${API}/voice-admin/agents?active_only=true`);
        setProfiles(response.data.profiles || []);
        if (response.data.profiles?.length > 0) {
          setSelectedProfileId(response.data.profiles[0].id);
        }
      } catch (error) {
        console.error('Error fetching profiles:', error);
      }
    };
    fetchProfiles();
  }, []);

  const handleSendMessage = async () => {
    if (!userMessage.trim() || !selectedProfileId) {
      toast.error('Please select an agent and enter a message');
      return;
    }

    setIsRunning(true);
    try {
      const response = await axios.post(`${API}/voice-admin/test-harness`, {
        agent_profile_id: selectedProfileId,
        user_message: userMessage,
        conversation_history: conversationHistory,
        include_kb_retrieval: true
      });

      const result = response.data;
      setLastResult(result);

      // Add to conversation history
      setConversationHistory([
        ...conversationHistory,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: result.agent_response }
      ]);

      setUserMessage('');
    } catch (error) {
      console.error('Error running test:', error);
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setIsRunning(false);
    }
  };

  const handleClearConversation = () => {
    setConversationHistory([]);
    setLastResult(null);
  };

  const selectedProfile = profiles.find(p => p.id === selectedProfileId);

  return (
    <div className="grid grid-cols-3 gap-4 h-[600px]">
      {/* Conversation Panel */}
      <div className="col-span-2 border rounded-lg flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select Agent" />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProfile && (
              <Badge variant="outline" className="capitalize">
                {selectedProfile.role}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleClearConversation}>
            Clear
          </Button>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {conversationHistory.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Start a test conversation</p>
                <p className="text-xs mt-1">Messages will appear here</p>
              </div>
            )}
            
            {conversationHistory.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-3 border-t">
          <div className="flex gap-2">
            <Input
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder="Type a test message..."
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              disabled={isRunning}
            />
            <Button onClick={handleSendMessage} disabled={isRunning || !selectedProfileId}>
              {isRunning ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Details Panel */}
      <div className="border rounded-lg flex flex-col">
        <div className="p-3 border-b">
          <h3 className="font-medium text-sm">Test Details</h3>
        </div>
        
        <ScrollArea className="flex-1 p-3">
          {lastResult ? (
            <div className="space-y-4">
              {/* Retrieved KB */}
              <div>
                <Label className="text-xs text-muted-foreground">Retrieved KB Items</Label>
                {lastResult.retrieved_kb_items?.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {lastResult.retrieved_kb_items.map((item, i) => (
                      <div key={i} className="p-2 bg-green-500/10 rounded-lg">
                        <p className="text-xs font-medium">{item.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {item.content}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">No KB items retrieved</p>
                )}
              </div>

              {/* Tools */}
              <div>
                <Label className="text-xs text-muted-foreground">Tools Would Call</Label>
                {lastResult.tools_would_call?.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {lastResult.tools_would_call.map((tool, i) => (
                      <div key={i} className="p-2 bg-yellow-500/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-3 w-3 text-yellow-600" />
                          <p className="text-xs font-medium">{tool.name}</p>
                          <Badge variant="outline" className="text-[10px]">dry-run</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{tool.reason}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">No tools would be called</p>
                )}
              </div>

              {/* Reasoning */}
              {lastResult.reasoning && (
                <div>
                  <Label className="text-xs text-muted-foreground">Reasoning</Label>
                  <p className="text-xs mt-1">{lastResult.reasoning}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FlaskConical className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Run a test to see details</p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

// ==========================================
// Main Component
// ==========================================
const RhinoVoice = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('agents');
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check permissions
  const canView = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'ai_assistant' ||
                  user?.role === 'leadership' || user?.role === 'call_center_manager' ||
                  user?.role === 'ceo' || user?.role === 'president' || user?.role === 'president_&_ceo';
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'ai_assistant';

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, configRes] = await Promise.all([
        axios.get(`${API}/voice-admin/stats`),
        axios.get(`${API}/voice-admin/config`)
      ]);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!canView) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Card className="p-8 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              You don&apos;t have permission to view this page.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mic className="h-6 w-6 text-primary" />
              Rhino-1 Voice
            </h1>
            <p className="text-muted-foreground text-sm">
              Configure AI voice agents, knowledge base, and tools
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline" 
              className={config?.voice_kb_enabled ? 
                'bg-green-500/10 text-green-500 border-green-500/30' : 
                'bg-gray-500/10 text-gray-500 border-gray-500/30'}
            >
              KB: {config?.voice_kb_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Badge 
              variant="outline" 
              className={config?.voice_tts_enabled ? 
                'bg-green-500/10 text-green-500 border-green-500/30' : 
                'bg-gray-500/10 text-gray-500 border-gray-500/30'}
            >
              TTS: {config?.voice_tts_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Button variant="ghost" size="icon" onClick={fetchStats}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <StatsOverview stats={stats} config={config} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-xl">
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Agents
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Knowledge
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Tools
            </TabsTrigger>
            <TabsTrigger value="test" className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" />
              Test
            </TabsTrigger>
          </TabsList>

          <TabsContent value="agents" className="mt-6">
            <AgentProfilesTab onRefresh={fetchStats} />
          </TabsContent>

          <TabsContent value="knowledge" className="mt-6">
            <KnowledgeBaseTab onRefresh={fetchStats} />
          </TabsContent>

          <TabsContent value="tools" className="mt-6">
            <ToolsTab onRefresh={fetchStats} />
          </TabsContent>

          <TabsContent value="test" className="mt-6">
            <TestHarnessTab />
          </TabsContent>
        </Tabs>

        {!canEdit && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <p className="text-sm">
                You have view-only access. Contact an admin to make changes.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default RhinoVoice;
